import React from "react";
import { theme } from "../../theme";

/**
 * Error Boundary component to catch and display React component errors gracefully
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
    this.setState({
      error,
      errorInfo
    });

  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: theme.spacing.xl,
            textAlign: "center",
            background: theme.colors.gradient.card,
            borderRadius: theme.borderRadius.lg,
            border: `2px solid ${theme.colors.danger}`,
            margin: theme.spacing.lg,
          }}
        >
          <div
            style={{
              fontSize: "48px",
              marginBottom: theme.spacing.md,
            }}
          >
            ️
          </div>
          <h3
            style={{
              color: theme.colors.text.primary,
              fontSize: theme.typography.fontSize.xl,
              fontWeight: theme.typography.fontWeight.bold,
              marginBottom: theme.spacing.sm,
            }}
          >
            Something went wrong
          </h3>
          <p
            style={{
              color: theme.colors.text.secondary,
              fontSize: theme.typography.fontSize.base,
              marginBottom: theme.spacing.lg,
            }}
          >
            {this.props.message || "An unexpected error occurred. Please try refreshing the page."}
          </p>
          {process.env.NODE_ENV === "development" && this.state.error && (
            <details
              style={{
                textAlign: "left",
                marginBottom: theme.spacing.md,
                padding: theme.spacing.md,
                background: theme.colors.background.dark,
                borderRadius: theme.borderRadius.md,
                color: theme.colors.text.muted,
                fontSize: theme.typography.fontSize.sm,
                maxHeight: "200px",
                overflow: "auto",
              }}
            >
              <summary style={{ cursor: "pointer", marginBottom: theme.spacing.sm }}>
                Error Details (Development Only)
              </summary>
              <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
          <div style={{ display: "flex", gap: theme.spacing.md, justifyContent: "center" }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: theme.spacing.sm + " " + theme.spacing.lg,
                background: theme.colors.primary,
                color: theme.colors.text.primary,
                border: "none",
                borderRadius: theme.borderRadius.md,
                cursor: "pointer",
                fontSize: theme.typography.fontSize.base,
                fontWeight: theme.typography.fontWeight.medium,
                transition: theme.transitions.normal,
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = "0.9";
                e.target.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = "1";
                e.target.style.transform = "translateY(0)";
              }}
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: theme.spacing.sm + " " + theme.spacing.lg,
                background: theme.colors.background.hover,
                color: theme.colors.text.primary,
                border: `1px solid ${theme.colors.border.default}`,
                borderRadius: theme.borderRadius.md,
                cursor: "pointer",
                fontSize: theme.typography.fontSize.base,
                fontWeight: theme.typography.fontWeight.medium,
                transition: theme.transitions.normal,
              }}
              onMouseEnter={(e) => {
                e.target.style.background = theme.colors.background.light;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = theme.colors.background.hover;
              }}
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
